import { svgEl } from '../dom.js';

/**
 * Brand radar-hexagon logo. Mirrors scripts/generate-brand-assets.mjs output
 * but inlined in the SPA so the header and debrief can show it without a
 * second network request. Colours use CSS custom properties so the logo
 * adapts to light / dark themes automatically.
 *
 * Sizes: default 28px. Passes all children via createElementNS so the
 * shapes actually paint (createElement('svg') would not).
 */
export function BrandLogo(size = 28): SVGSVGElement {
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size * 0.40;
  const rInner = size * 0.22;
  const rMid = size * 0.30;
  const strokeOuter = Math.max(1.2, size * 0.055);
  const strokeMid = strokeOuter * 0.5;
  const strokeSpoke = strokeOuter * 0.32;

  const points = (r: number) =>
    Array.from({ length: 7 }, (_, i) => {
      const a = -Math.PI / 2 + i * ((2 * Math.PI) / 7);
      return `${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`;
    }).join(' ');

  const svg = svgEl('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    viewBox: `0 0 ${size} ${size}`,
    width: size,
    height: size,
    class: 'brand-logo',
    role: 'img',
    'aria-label': 'Allot',
  });

  // Spokes — 7 thin radii, drawn first so the rings overlay them.
  for (let i = 0; i < 7; i++) {
    const a = -Math.PI / 2 + i * ((2 * Math.PI) / 7);
    const x = cx + rOuter * Math.cos(a);
    const y = cy + rOuter * Math.sin(a);
    svg.appendChild(
      svgEl('line', {
        x1: cx,
        y1: cy,
        x2: x.toFixed(2),
        y2: y.toFixed(2),
        stroke: 'currentColor',
        'stroke-opacity': '0.25',
        'stroke-width': strokeSpoke,
      }),
    );
  }

  // Accent-filled inner heptagon (warm copper).
  svg.appendChild(
    svgEl('polygon', {
      points: points(rInner),
      fill: 'var(--accent, #c86b3e)',
      'fill-opacity': '0.24',
      stroke: 'var(--accent, #c86b3e)',
      'stroke-opacity': '0.7',
      'stroke-width': strokeMid,
      'stroke-linejoin': 'round',
    }),
  );

  // Middle ring.
  svg.appendChild(
    svgEl('polygon', {
      points: points(rMid),
      fill: 'none',
      stroke: 'currentColor',
      'stroke-opacity': '0.5',
      'stroke-width': strokeMid,
      'stroke-linejoin': 'round',
    }),
  );

  // Outer ring — the silhouette.
  svg.appendChild(
    svgEl('polygon', {
      points: points(rOuter),
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': strokeOuter,
      'stroke-linejoin': 'round',
    }),
  );

  return svg;
}
